import React, { useState, useEffect } from "react";
import { api } from "../shared/axiosInstance";
import CandidateList from "./CandidateList";
import JobPostModal from "./JobPostModal";

const JobPostDetails = ({ job }) => {
    const [candidates, setCandidates] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [loading, setLoading] = useState(true);
    const [updatedJob, setUpdatedJob] = useState(job);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        matchCategory: [],
        reviewStatus: [],
        applied: null,
        email: ""
    });
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
    const [refreshing, setRefreshing] = useState(false);
    const [showIneligible, setShowIneligible] = useState(false);
    const [ineligibleCount, setIneligibleCount] = useState(0);

    // Fetch candidates
    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.matchCategory.length) {
                params.append("matchCategory", filters.matchCategory.join(","));
            }
            if (filters.reviewStatus.length) params.append("reviewStatus", filters.reviewStatus.join(","));
            if (filters.applied !== null) params.append("applied", filters.applied);
            if (filters.email) params.append("email", filters.email);
            params.append("page", pagination.page);
            params.append("pageSize", pagination.pageSize);
            if (showIneligible) params.append("showIneligible", "true");

            const url = `/jobs/${job._id}/filtered-candidates?${params.toString()}`;

            const res = await api.get(url);
            setTotalMatches(res.data?.total || 0); // ✅ Needed for correct pagination display
            setCandidates(res.data?.candidates || []);
            setIneligibleCount(res.data?.ineligibleCount || 0);
        } catch (err) {
            console.error("❌ Error fetching filtered candidates:", err);
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (job?._id) {
            fetchCandidates();
        }
    }, [job._id, filters, pagination, showIneligible]);

    useEffect(() => {
        const refreshAndFetch = async () => {
            if (job?._id) {
                setRefreshing(true);

                try {
                    // Trigger backend refresh
                    await api.post(`/matches/refresh/${job._id}`);
                } catch (err) {
                    console.warn("⚠️ Match refresh failed:", err.message);
                }

                await fetchCandidates(); // initial fetch

                // Optional retry after 2s if still empty
                if (candidates.length === 0) {
                    setTimeout(async () => {
                        await fetchCandidates();
                        setRefreshing(false);
                    }, 2000);
                } else {
                    setRefreshing(false);
                }
            }
        };

        refreshAndFetch();
    }, [job]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, [filters]);

    // Handle review status change from CandidateList
    const handleReviewStatusChange = (userId, newStatus) => {
        setCandidates((prev) =>
            prev.map((m) =>
                m.matchedUser._id === userId ? { ...m, reviewStatus: newStatus } : m
            )
        );
    };
// just before return (
    const filtersAreActive =
        filters.matchCategory.length > 0 ||
        filters.reviewStatus.length > 0 ||
        filters.applied !== null ||
        filters.email !== "";

    return (
        <div className="bg-gray-800 p-4 rounded-lg w-full">
            <h2 className="text-xl font-bold text-white">{updatedJob.title}</h2>
            <p className="text-gray-300">Status: <strong>{updatedJob.status}</strong></p>
            <p className="text-gray-300">
                Salary Range: <strong>
                {updatedJob.salaryMin?.toLocaleString()} – {updatedJob.salaryMax?.toLocaleString()} ({updatedJob.salaryType})
            </strong>
            </p>
            <p className="text-gray-300">
                Location: <strong>{updatedJob.locationType}</strong>
            </p>

            {["Hybrid", "Onsite"].includes(updatedJob.locationType) && (
                <>
                    <p className="text-gray-300">Postal Code: <strong>{updatedJob.postalCode || "N/A"}</strong></p>
                    <p className="text-gray-300">Country: <strong>{updatedJob.country || "N/A"}</strong></p>
                </>
            )}

            {isModalOpen && (
                <JobPostModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={(newJob) => setUpdatedJob(newJob)}
                    job={updatedJob}
                />
            )}

            <h3 className="text-lg font-bold text-white mt-4">Targets</h3>
            {Array.isArray(updatedJob.targets) && updatedJob.targets.length > 0 ? (
                <ul className="text-gray-300 space-y-2">
                    {updatedJob.targets.map((target, index) => (
                        <li key={index} className="flex justify-between items-center bg-gray-700 p-2 rounded-md">
                            <div>{target.firstName} {target.lastName} - {target.email}</div>
                            <span className={`px-3 py-1 rounded text-white ${
                                target.status === "incomplete" ? "bg-red-500" :
                                    target.status === "pre-invite" ? "bg-gray-500" :
                                        target.status === "invited" ? "bg-yellow-500" :
                                            target.status === "taking assessment" ? "bg-blue-500" :
                                                "bg-green-500"
                            }`}>
                {target.status.replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-gray-400">No targets added.</p>
            )}
            <div className="mt-6 mb-4">
                <div className="flex flex-wrap gap-2 mb-2">
                    {["perfect", "great", "potential"].map((cat) => (
                        <button
                            key={cat}
                            className={`px-3 py-1 rounded-full border text-sm ${
                                filters.matchCategory?.includes(cat) ? "bg-blue-500 text-white" : "bg-gray-700 text-gray-300"
                            }`}
                            onClick={() =>
                                setFilters((prev) => {
                                    const isSelected = prev.matchCategory?.includes(cat);
                                    const newCategories = isSelected
                                        ? prev.matchCategory.filter((c) => c !== cat)
                                        : [...(prev.matchCategory || []), cat];
                                    return { ...prev, matchCategory: newCategories };
                                })
                            }
                        >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </button>
                    ))}
                    <button
                        className={`px-3 py-1 rounded-full border text-sm ${
                            filters.applied === true ? "bg-green-500 text-white" : "bg-gray-700 text-gray-300"
                        }`}
                        onClick={() =>
                            setFilters((prev) => ({
                                ...prev,
                                applied: prev.applied === true ? null : true
                            }))
                        }
                    >
                        Applied
                    </button>
                    <button
                        className={`px-3 py-1 rounded-full border text-sm ${
                            filters.applied === false ? "bg-yellow-500 text-white" : "bg-gray-700 text-gray-300"
                        }`}
                        onClick={() =>
                            setFilters((prev) => ({
                                ...prev,
                                applied: prev.applied === false ? null : false
                            }))
                        }
                    >
                        Not Applied
                    </button>

                    {["moveForward", "rejected", "unreviewed"].map(status => (
                        <button
                            key={status}
                            className={`px-3 py-1 rounded-full border text-sm ${
                                filters.reviewStatus.includes(status)
                                    ? "bg-purple-500 text-white"
                                    : "bg-gray-700 text-gray-300"
                            }`}
                            onClick={() =>
                                setFilters((prev) => ({
                                    ...prev,
                                    reviewStatus: prev.reviewStatus.includes(status)
                                        ? prev.reviewStatus.filter(s => s !== status)
                                        : [...prev.reviewStatus, status]
                                }))
                            }
                        >
                            {status === "moveForward" ? "Move Forward" : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}

                    <button
                        className="ml-auto px-3 py-1 rounded border text-sm bg-gray-600 text-white"
                        onClick={() => {
                            setFilters({
                                matchCategory: [],
                                applied: null,
                                reviewStatus: [],
                                email: ""
                            });
                            setPagination({ page: 1, pageSize: 10 });
                        }}
                    >
                        {filtersAreActive ? "Clear Filters" : "All Matches"}
                    </button>

                    <button
                        className={`px-3 py-1 rounded border text-sm ${
                            showIneligible ? 'bg-orange text-darkblue' : 'bg-gray-700 text-gray-300'
                        }`}
                        onClick={() => setShowIneligible(prev => !prev)}
                    >
                        {showIneligible ? 'Hide ineligible' : `Show ineligible (${ineligibleCount})`}
                    </button>

                </div>

                <input
                    type="text"
                    placeholder="Search by email..."
                    value={filters.email}
                    onChange={(e) => {
                        setFilters((prev) => ({
                            ...prev,
                            email: e.target.value
                        }));
                    }}
                    className="w-full p-2 rounded bg-gray-700 text-white placeholder-gray-400"
                />
            </div>

            {refreshing && (
                <div className="text-center text-yellow-400 mb-4 animate-pulse">
                    Refreshing candidate matches...
                </div>
            )}

            {loading ? (
                <p className="text-gray-400">Loading candidates...</p>
            ) : (
                <CandidateList
                    candidates={candidates}
                    targets={updatedJob.targets}
                    jobId={job._id}
                    loading={loading}
                    filters={filters}
                    setFilters={setFilters}
                    pagination={pagination}
                    setPagination={setPagination}
                    currentPage={pagination.page}
                    pageSize={pagination.pageSize}
                    totalMatches={totalMatches}
                    onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
                    onReviewStatusChange={handleReviewStatusChange}
                />
            )}
        </div>
    );
};

export default JobPostDetails;
